/* import __COLOCATED_TEMPLATE__ from './product-filters.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type ImportSource from 'embercom/models/import-service/import-source';
import { SyncBehaviorType } from 'embercom/models/import-service/import-source';

interface Signature {
  Args: { modal: any; importSource: ImportSource; onModalClose: () => void };
  Element: null;
}

type LogicalOperator = 'and' | 'or';

interface SelectItem {
  text: string;
  value: string;
}

interface ProductCondition {
  attribute: string;
  comparison: string;
  value: string | null;
}

const PRODUCT_FIELDS: Array<{ key: string; operators: string[] }> = [
  {
    key: 'vendor',
    operators: ['eq', 'ne', 'contains', 'not_contains', 'starts_with', 'ends_with', 'known'],
  },
  {
    key: 'product_type',
    operators: ['eq', 'ne', 'contains', 'not_contains', 'starts_with', 'ends_with', 'known'],
  },
  {
    key: 'tags_tag_names',
    operators: ['eq', 'ne', 'contains', 'not_contains', 'known'],
  },
];

const LEGACY_DESCRIPTOR_TO_FIELD: Record<string, string> = {
  basic_vendor: 'vendor',
  basic_product_type: 'product_type',
  basic_tags_tag_names: 'tags_tag_names',
};

const FIELD_TRANSLATION_KEYS: Record<string, string> = {
  vendor: 'knowledge-hub.sync-modal.shopify.product-filters.field-vendor',
  product_type: 'knowledge-hub.sync-modal.shopify.product-filters.field-product-type',
  tags_tag_names: 'knowledge-hub.sync-modal.shopify.product-filters.field-tags-tag-names',
};

const OPERATOR_TRANSLATION_KEYS: Record<string, string> = {
  eq: 'knowledge-hub.sync-modal.shopify.product-filters.operator-eq',
  ne: 'knowledge-hub.sync-modal.shopify.product-filters.operator-ne',
  contains: 'knowledge-hub.sync-modal.shopify.product-filters.operator-contains',
  not_contains: 'knowledge-hub.sync-modal.shopify.product-filters.operator-not-contains',
  starts_with: 'knowledge-hub.sync-modal.shopify.product-filters.operator-starts-with',
  ends_with: 'knowledge-hub.sync-modal.shopify.product-filters.operator-ends-with',
  known: 'knowledge-hub.sync-modal.shopify.product-filters.operator-known',
};

function emptyCondition(): ProductCondition {
  return { attribute: '', comparison: '', value: null };
}

export default class ShopifyProductFilters extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare notificationsService: any;

  @tracked logicalOperator: LogicalOperator = 'and';
  @tracked conditions: ProductCondition[] = [emptyCondition()];
  @tracked isSaving = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    let rules = args.importSource?.prefilterRules;
    if (rules) {
      let predicates = rules.predicates ?? (rules.descriptor_name ? [rules] : []);
      if (predicates.length > 0) {
        this.logicalOperator = rules.type === 'or' ? 'or' : 'and';
        this.conditions = predicates.map(
          (p: {
            descriptor_name?: string;
            attribute?: string;
            comparison?: string;
            value?: string;
          }) => {
            let rawAttribute = p.descriptor_name ?? p.attribute ?? '';
            let attribute = LEGACY_DESCRIPTOR_TO_FIELD[rawAttribute] ?? rawAttribute;
            return {
              attribute,
              comparison: p.comparison ?? 'eq',
              value: p.value ?? null,
            };
          },
        );
      }
    }
  }

  get hasMultipleConditions(): boolean {
    return this.conditions.length > 1;
  }

  get logicalOperatorOptions(): SelectItem[] {
    return [
      {
        text: this.intl.t('knowledge-hub.sync-modal.shopify.product-filters.logical-operator-and'),
        value: 'and',
      },
      {
        text: this.intl.t('knowledge-hub.sync-modal.shopify.product-filters.logical-operator-or'),
        value: 'or',
      },
    ];
  }

  get availableFields(): SelectItem[] {
    return PRODUCT_FIELDS.map((field) => ({
      text: this.intl.t(FIELD_TRANSLATION_KEYS[field.key]!),
      value: field.key,
    }));
  }

  @action
  operatorsForField(fieldKey: string | undefined): SelectItem[] {
    if (!fieldKey) {
      return [];
    }
    let field = PRODUCT_FIELDS.find((f) => f.key === fieldKey);
    if (!field) {
      return [];
    }
    return field.operators.map((op) => ({
      text: this.intl.t(OPERATOR_TRANSLATION_KEYS[op]!),
      value: op,
    }));
  }

  private get hasIncompleteCondition(): boolean {
    return this.conditions.some((c) => {
      if (!c.attribute) {
        return true;
      }
      if (!c.comparison) {
        return true;
      }
      if (
        c.comparison !== 'known' &&
        (c.value === null || c.value === undefined || c.value === '')
      ) {
        return true;
      }
      return false;
    });
  }

  get isSaveDisabled(): boolean {
    return this.isSaving || this.hasIncompleteCondition;
  }

  get isAddConditionDisabled(): boolean {
    return this.isSaving || this.hasIncompleteCondition;
  }

  get hasFilters(): boolean {
    return this.conditions.some((c) => c.attribute && c.comparison);
  }

  @action
  addCondition() {
    this.conditions = [...this.conditions, emptyCondition()];
  }

  @action
  removeCondition(index: number) {
    let updated = [...this.conditions];
    updated.splice(index, 1);
    this.conditions = updated;
  }

  @action
  updateConditionField(index: number, fieldKey: string) {
    let updated = [...this.conditions];
    updated[index] = { attribute: fieldKey, comparison: '', value: null };
    this.conditions = updated;
  }

  @action
  updateConditionOperator(index: number, operator: string) {
    let updated = [...this.conditions];
    let condition = { ...updated[index]! };
    condition.comparison = operator;
    if (operator === 'known') {
      condition.value = null;
    }
    updated[index] = condition;
    this.conditions = updated;
  }

  @action
  updateConditionValue(index: number, event: InputEvent) {
    let updated = [...this.conditions];
    updated[index] = { ...updated[index]!, value: (event.target as HTMLInputElement).value };
    this.conditions = updated;
  }

  @action
  updateLogicalOperator(op: string) {
    this.logicalOperator = op as LogicalOperator;
  }

  private buildPredicates() {
    return this.conditions
      .filter((c) => c.attribute && c.comparison)
      .map((c) => ({
        attribute: c.attribute,
        comparison: c.comparison,
        value: c.comparison === 'known' ? null : (c.value?.trim() ?? null),
      }));
  }

  @action async saveFilters() {
    this.isSaving = true;
    try {
      await this.knowledgeHubService.updatePrefilterRules(
        this.args.importSource.id,
        this.buildPredicates(),
        this.logicalOperator,
      );
      await this.args.importSource.reload();
      await this.knowledgeHubService.startImport(this.args.importSource.id, SyncBehaviorType.Sync);
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.sync-modal.shopify.product-filters.save-success'),
      );
      this.args.onModalClose();
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.shopify.product-filters.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }

  @action async clearFilters() {
    this.isSaving = true;
    try {
      await this.knowledgeHubService.updatePrefilterRules(this.args.importSource.id, []);
      await this.args.importSource.reload();
      this.logicalOperator = 'and';
      this.conditions = [emptyCondition()];
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.sync-modal.shopify.product-filters.clear-success'),
      );
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.shopify.product-filters.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Shopify::ProductFilters': typeof ShopifyProductFilters;
    'knowledge-hub/setup-modal/sync-modal/shopify/product-filters': typeof ShopifyProductFilters;
  }
}
